<template>
  <div class="tab-container">
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells><app-button @click.native="onOpenCreateModal(null)">Create receipt</app-button></app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__verification_code><div class="table-default__left">Verification code</div></template>
        <template #h__supplier><div class="table-default__left">Supplier</div></template>
        <template #source="{ row }">{{ row.source.name }}</template>
        <template #verification_code="{ row }">
          <div class="table-default__left">
            <button
              @click="onCopyToClipboard(row.verification_link)"
              class="table-default__copy"
              :class="{'table-default__copy--disabled': !row.verification_link}"
            >
              {{ row.verification_code }}
            </button>
          </div>
        </template>
        <template #date="{ row }"><span class="table-default__nowrap">{{ dateFormat(row.date) }}</span></template>
        <template #time="{ row }">{{ row.time }}</template>
        <template #supplier="{ row }">
          <router-link
            :to="{ name: 'administrative-suppliers-detail-purchases', params: { id: row.supplier.id } }"
            class="table-default__link-border"
          >
            {{ row.supplier.trade_name }}
          </router-link>
        </template>
        <template #amount_total="{ row }">{{ formatAmount(row.amount_total) }}</template>
        <template #amount_vat_exc="{ row }">{{ formatAmount(row.amount_vat_exc) }}</template>
        <template #amount_vat="{ row }">{{ formatAmount(row.amount_vat) }}</template>
        <template #non_creditable_amount="{ row }">{{ formatAmount(row.non_creditable_amount) }}</template>
        <template #amount_exempted="{ row }">{{ formatAmount(row.amount_exempted) }}</template>
        <template #receipt_copy="{ row }">
          <div class="table-default__controls">
            <button
              @click="onDownloadFile(row.receipt_copy, row)"
              type="button"
              :disabled="!row.receipt_copy"
            >
              <img src="@/assets/img/download-table-blue-icon.svg" alt="">
            </button>
          </div>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onOpenCreateModal(row)" type="button"><img src="@/assets/img/edit-table-icon.svg" alt="Edit"></button>
            <button @click="onShowDeleteModal(row)" type="button"><img src="@/assets/img/delete-table-icon.svg" alt="Delete"></button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
      name="receipt-modal"
      :width="700"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('receipt-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">{{ receipt_to_action ? 'Update' : 'Create' }} receipt</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-grid>
              <template #item-1>
                <app-form-group label="Territory" required class="form-group--mh">
                  <app-cells :indent="false">
                    <app-radio
                      v-for="radio of tax_zones_options"
                      :key="radio.name"
                      v-model="form.zone"
                      :value="radio.code"
                      name="tax_zones"
                      :checked="form.zone === radio.code"
                    >
                      <template #label>{{ radio.name }}</template>
                    </app-radio>
                  </app-cells>
                  <template #error>
                    <div v-if="$v.form.zone.$dirty && !$v.form.zone.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Legal entity" required>
                  <app-select
                    v-model="form.legal_entity"
                    :options="legal_entity_options"
                    :clearable="false"
                    label="short_name"
                    placeholder="Legal entity"
                    :error="$v.form.legal_entity.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.legal_entity.$dirty && !$v.form.legal_entity.required">Required field</div>
                  </template>
                </app-form-group>
                <app-cells :indent="false" :wrap="false">
                  <app-form-group label="Date" required>
                    <app-date-picker
                      v-model="form.date"
                      :clearable="false"
                      placeholder="Date"
                      :error="$v.form.date.$error"
                    />
                    <template #error>
                      <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group label="Time" required>
                    <app-date-picker
                      v-model="form.time"
                      :clearable="false"
                      format="HH:mm:ss"
                      value-type="HH:mm:ss"
                      type="time"
                      placeholder="Time"
                      :error="$v.form.time.$error"
                    />
                    <template #error>
                      <div v-if="$v.form.time.$dirty && !$v.form.time.required">Required field</div>
                    </template>
                  </app-form-group>
                </app-cells>
                <app-form-group label="Verification code" label-for="code" required>
                  <app-input
                    v-model="form.verification_code"
                    :error="$v.form.verification_code.$error"
                    id="code"
                    placeholder="Verification code"
                    name="code"
                  />
                  <template #error>
                    <div v-if="$v.form.verification_code.$dirty && !$v.form.verification_code.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Supplier" required>
                  <app-select
                    @search="onSupplierSearch"
                    v-model="form.supplier"
                    :options="supplier_options"
                    :clearable="false"
                    label="trade_name"
                    placeholder="Supplier"
                    :error="$v.form.supplier.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.supplier.$dirty && !$v.form.supplier.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Receipt copy" required>
                  <app-uploader
                    v-model="form.receipt_copy"
                    :extensions="['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip', 'application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp']"
                    formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
                    class="custom-uploader--flex"
                  />
                  <template #error>
                    <div v-if="$v.form.receipt_copy.$dirty && !$v.form.receipt_copy.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Total amount" label-for="amount" required>
                  <app-fin-number
                    v-model="form.amount_total"
                    :error="$v.form.amount_total.$error"
                    id="amount"
                    name="amount"
                  />
                  <template #error>
                    <div v-if="$v.form.amount_total.$dirty && !$v.form.amount_total.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Excluded VAT" label-for="amount_vat_exc" required>
                  <app-fin-number
                    v-model="form.amount_vat_exc"
                    :error="$v.form.amount_vat_exc.$error"
                    id="amount_vat_exc"
                    name="amount_vat_exc"
                  />
                  <template #error>
                    <div v-if="$v.form.amount_vat_exc.$dirty && !$v.form.amount_vat_exc.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="VAT" label-for="amount_vat" required>
                  <app-fin-number
                    v-model="form.amount_vat"
                    :error="$v.form.amount_vat.$error"
                    id="amount_vat"
                    name="amount_vat"
                  />
                  <template #error>
                    <div v-if="$v.form.amount_vat.$dirty && !$v.form.amount_vat.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Exempted" label-for="amount_exempted" required>
                  <app-fin-number
                    v-model="form.amount_exempted"
                    :error="$v.form.amount_exempted.$error"
                    id="amount_exempted"
                    name="amount_exempted"
                  />
                  <template #error>
                    <div v-if="$v.form.amount_exempted.$dirty && !$v.form.amount_exempted.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Non-creditable" label-for="non_creditable_amount" required>
                  <app-fin-number
                    v-model="form.non_creditable_amount"
                    :error="$v.form.non_creditable_amount.$error"
                    id="non_creditable_amount"
                    name="non_creditable_amount"
                  />
                  <template #error>
                    <div v-if="$v.form.non_creditable_amount.$dirty && !$v.form.non_creditable_amount.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
            </app-grid>
            <app-form-group label="Notes" label-for="description">
              <app-textarea
                v-model="form.notes"
                rows="5"
                placeholder="Write a note"
                id="description"
                name="description"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">{{ receipt_to_action ? 'Save' : 'Create' }}</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete receipt
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this receipt?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteReceipt" ref="deleteEvent">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteFinanceEFDReceipt,
  getAdministrativeLegalEntities,
  getAdministrativeSuppliers,
  getFinanceSuppliersEFDReceipts,
  getFinanceTaxZones,
  patchFinanceEFDReceipt,
  postFinanceFundRequestsEfdReceipts,
} from '@/http'
import { required } from 'vuelidate/lib/validators'
import { format, parseISO } from 'date-fns'
import axios from 'axios'
import debounce from 'lodash/debounce'

export default {
  name: 'FinanceFundRequestsDetailReceipts',
  props: ['requestData'],
  data() {
    return {
      page_loaded: false,
      receipt_to_action: null,
      form: {},
      supplier_options: [],
      legal_entity_options: [],
      tax_zones_options: [],
      table: {
        items: [],
        columns: ['receipt_copy', 'source', 'verification_code', 'date', 'time', 'legal_entity.name', 'supplier', 'amount_total', 'amount_vat_exc', 'amount_vat', 'non_creditable_amount', 'amount_exempted', 'controls'],
        options: {
          headings: {
            'receipt_copy': 'Receipt',
            'source': 'Source',
            'verification_code': 'Verification code',
            'date': 'Date',
            'time': 'Time',
            'legal_entity.name': 'Legal entity',
            'supplier': 'Supplier',
            'amount_total': 'Total amount',
            'amount_vat_exc': 'VAT Exclude',
            'amount_vat': 'VAT',
            'non_creditable_amount': 'Non-creditable',
            'amount_exempted': 'Exempted',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
    }
  },
  validations: {
    form: {
      zone: { required },
      legal_entity: { required },
      supplier: { required },
      date: { required },
      time: { required },
      verification_code: { required },
      amount_total: { required },
      amount_vat_exc: { required },
      amount_vat: { required },
      amount_exempted: { required },
      receipt_copy: { required },
      non_creditable_amount: { required },
    }
  },
  created() {
    this.fetchItems()
    getAdministrativeLegalEntities().then(res => { this.legal_entity_options = res.data })
    getFinanceTaxZones().then(res => { this.tax_zones_options = res.data })
  },
  methods: {
    fetchItems() {
      this.table_preloader = true
      getFinanceSuppliersEFDReceipts({
        page: this.pagination.page,
        page_size: 20,
        fund_request: Number(this.$route.params.id)
      })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.table_preloader = false
          this.page_loaded = true
        })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (!this.receipt_to_action) {
        postFinanceFundRequestsEfdReceipts(Number(this.$route.params.id), this.normalizeForm())
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Receipt created',
            })
            this.$modal.hide('receipt-modal')
          })
          .catch(() => {
            this.$refs.submit.preload = false
          })
      } else {
        patchFinanceEFDReceipt(this.receipt_to_action.id, this.normalizeForm())
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Receipt updated',
            })
            this.$modal.hide('receipt-modal')
          })
          .catch(() => {
            this.$refs.submit.preload = false
          })
      }
    },
    normalizeForm() {
      const normalizedForm = { ...this.form }

      normalizedForm.receipt_copy = normalizedForm.receipt_copy.id
      normalizedForm.supplier = normalizedForm.supplier.id
      normalizedForm.legal_entity = normalizedForm.legal_entity.code
      delete normalizedForm.source

      return normalizedForm
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onOpenCreateModal(receipt) {
      this.receipt_to_action = receipt
      if (!receipt) {
        this.form = {
          legal_entity: this.requestData.legal_entity,
        }
      } else {
        this.form = {
          zone: receipt.zone.code,
          legal_entity: receipt.legal_entity,
          supplier: receipt.supplier,
          date: receipt.date,
          time: receipt.time,
          verification_code: receipt.verification_code,
          amount_total: receipt.amount_total,
          amount_vat_exc: receipt.amount_vat_exc,
          amount_vat: receipt.amount_vat,
          amount_exempted: receipt.amount_exempted,
          non_creditable_amount: receipt.non_creditable_amount,
          receipt_copy: receipt.receipt_copy,
          notes: receipt.notes,
          source: receipt.source,
        }
      }
      this.$v.form.$reset()
      this.$modal.show('receipt-modal')
    },
    onShowDeleteModal(receipt) {
      this.receipt_to_action = receipt
      this.$modal.show('delete')
    },
    onDeleteReceipt() {
      this.$refs.deleteEvent.preload = true
      deleteFinanceEFDReceipt(this.receipt_to_action.id)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Receipt deleted',
          })
          this.fetchItems()
          this.$modal.hide('delete')
        })
        .catch(() => { this.$refs.deleteEvent.preload = false })
    },
    formatAmount(amount) {
      return new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(amount)
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm:ss') : '-'
    },
    onCopyToClipboard(string) {
      navigator.clipboard.writeText(string)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
    onDownloadFile(doc_obj, row) {
      axios.get(doc_obj.file, { responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          const extension = doc_obj.file_name.split('.').pop()
          const part_of_name = `${row.verification_code} - ${this.dateFormat(row.timestamp)} - ${row.legal_entity.name} - ${row.supplier.trade_name}`

          link.href = url
          link.setAttribute('download', `${part_of_name}.${extension}`)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(url)
        })
    },
    onSupplierSearch(search, loading) {
      loading(true)
      this.searchSupplier(loading, search, this)
    },
    searchSupplier: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.supplier_options = []
      } else {
        getAdministrativeSuppliers({search}).then(response => {
          vm.supplier_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>
